// Get started — where a new user lands on first login, and comes back to from the sidebar.
// The two main features first as large cards, then set-up rows (name, mailbox) that open Settings, then one row per other
// feature: what it is for, a "Show me" walkthrough of the real page (GuidedTour), and a tick once done.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  FiArrowRight,
  FiBell,
  FiBookmark,
  FiBriefcase,
  FiCheck,
  FiCreditCard,
  FiEdit3,
  FiLock,
  FiMail,
  FiSend,
  FiUser,
  FiUserPlus,
  FiUsers,
} from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { getUserSettings } from "../lib/userSettingsService";
import { getMailStatus } from "../lib/outreachService";
import { getStartedDone, INHOUSE_HIDDEN, markGetStarted, openOnPlan, TRIAL_OPEN, useGetStarted } from "../lib/getStartedService";
import { usePlan } from "../utils/usePlan";
import { useFindNotification } from "../context/FindNotificationContext";
import { useCredits } from "../utils/useCredits";
import { startTour } from "../components/GuidedTour";
import { DeviceSearch, Spark } from "../components/ui/icons";
import Skeleton from "../components/Skeleton";

// The two things scoutm8 is for, shown first and large.
const MAIN = [
  {
    key: "track",
    tour: "jobs",
    icon: FiBriefcase,
    title: "Track companies and their jobs",
    text: "Follow the firms you care about. Every role they post lands in one list, and we check for new ones each day.",
    learn: ["Add a firm and its careers page", "Pull roles from job boards too", "See all their open jobs in one place"],
    where: "Jobs",
  },
  {
    key: "search",
    tour: "people",
    icon: FiUserPlus,
    title: "Find candidates",
    text: "Describe the person you need, or paste a job description, and we search for people who match.",
    learn: ["Paste a job description or describe a person", "Check the brief before it runs", "Get a list of matching people"],
    where: "People",
  },
];

// Account set-up, done in Settings: each row opens its tab there.
const SETUP = [
  {
    key: "name",
    tab: "profile",
    icon: FiUser,
    title: "Your name",
    text: "Used to sign the emails you send to candidates.",
    cta: "Add your name",
    detail: ({ settings, savedName }) => [savedName, String(settings?.lastName || "").trim()].filter(Boolean).join(" "),
  },
  {
    key: "mailbox",
    tab: "mailbox",
    icon: FiMail,
    title: "Connect your mailbox",
    text: "So emails to candidates go out from your own address.",
    cta: "Connect",
    detail: ({ mail }) =>
      mail?.connected ? `${mail.provider === "google" ? "Gmail" : "Outlook"}${mail.emailAddress ? ` · ${mail.emailAddress}` : ""}` : null,
  },
];

// Everything else, in the order a new user would use it.
const STEPS = [
  {
    key: "results",
    tour: "results",
    icon: FiUsers,
    title: "Go through your search results",
    text: "How the list is ordered, how to open someone's profile and what to do when they aren't the right people.",
    where: "People",
    // Walks through a finished search, so it waits for the first one.
    needs: "search",
  },
  {
    key: "credits",
    tour: "credits",
    icon: FiCreditCard,
    title: "Track your spending",
    text: "Where your credits are, what they're worth in dollars and how long they'll last.",
    where: "Settings",
  },
  {
    key: "research",
    tour: "research",
    icon: DeviceSearch,
    title: "Research a company",
    text: "A firm's profile, hiring trends, contacts and news, in one place.",
    where: "Research",
  },
  {
    key: "save",
    tour: "saved",
    icon: FiBookmark,
    title: "Save jobs and firms",
    text: "Bookmark roles and companies you want to come back to.",
    where: "Jobs · Research",
  },
  {
    key: "outreach",
    tour: "outreach",
    icon: FiSend,
    title: "Reach out",
    text: "Shortlist candidates and email them from your own mailbox, with AI help on the draft.",
    where: "Outreach",
  },
  {
    key: "signature",
    tour: "signature",
    icon: FiEdit3,
    title: "Add your email signature",
    text: "Set it once and it goes at the bottom of every email you send.",
    where: "Settings",
  },
  {
    key: "prompts",
    tour: "prompts",
    icon: Spark,
    title: "Write emails your way with AI prompts",
    text: "Save how you like your outreach written, and the AI follows it for every email.",
    where: "Settings",
  },
  {
    key: "alerts",
    tour: "alerts",
    icon: FiBell,
    title: "Get daily job alerts",
    text: "One email each morning with new roles at the firms you follow.",
    where: "Settings",
    feature: "emailAlerts",
  },
];

export default function GetStartedPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const progress = useGetStarted();
  const { features, loading: planLoading } = usePlan();
  const { activeRun } = useFindNotification();
  const { balance } = useCredits();
  // Trial-level access (on a trial, after it ended, or a repeat sign-up), whatever the status row says.
  const onTrial = !planLoading && features.plan === "trial";
  const trialLocked = (key) => onTrial && !TRIAL_OPEN.has(key);
  // In-house has no agency steps at all; they drop out of the lists rather than showing locked.
  const inhouse = features.edition === "inhouse";
  const main = inhouse ? MAIN.filter((s) => !INHOUSE_HIDDEN.has(s.key)) : MAIN;
  const steps = inhouse ? STEPS.filter((s) => !INHOUSE_HIDDEN.has(s.key)) : STEPS;
  const [savedName, setSavedName] = useState(null); // null = not loaded yet
  const [mail, setMail] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!user?.sub) return;
    let live = true;
    getUserSettings(user.sub)
      .then((doc) => {
        if (!live) return;
        setSavedName((doc?.firstName || "").trim());
        setSettings(doc || {});
      })
      .catch(() => live && setSavedName(""));
    getMailStatus()
      .then((s) => live && setMail(s || { connected: false }))
      .catch(() => live && setMail({ connected: false }));
    return () => {
      live = false;
    };
  }, [user?.sub]);

  // Opening the page counts as seen, so a first login is only redirected here once.
  useEffect(() => {
    if (progress && !progress.seenAt) markGetStarted({ seen: true });
  }, [progress]);

  const done = getStartedDone(progress, { ...settings, firstName: savedName }, { mailbox: mail?.connected });
  // A walkthrough whose last click the account can't make swaps for a short one that says why:
  // searching and tracking stop at zero credits, one-at-a-time plans can't start a second search,
  // and Research lists only firms already tracked.
  const tourFor = (id) => {
    // A search needs 50 credits to start; tracking a company needs 2,000 up front (BFF MIN_BALANCE).
    if (id === "people" && balance != null && balance < 50) return "noCredits";
    if (id === "jobs" && balance != null && balance < 2000) return "noCredits";
    if (id === "people" && activeRun && activeRun.status !== "paused" && features.concurrentSearches <= 1) return "peopleRunning";
    if (id === "research" && !done.track) return "researchEmpty";
    return id;
  };
  const counted = openOnPlan(done, onTrial, features.edition);
  const total = Object.keys(counted).length;
  const count = Object.values(counted).filter(Boolean).length;
  const loading = !progress || savedName === null || !mail || planLoading;
  const planLocked = (s) => trialLocked(s.key) || (s.feature && !planLoading && !features[s.feature]);
  const waiting = (s) => Boolean(s.needs && !done[s.needs]);
  const nextKey = [...main, ...SETUP, ...steps].find((s) => !done[s.key] && !planLocked(s) && !waiting(s))?.key;

  return (
    <Root>
      <div className="gs-wrap">
        <header className="gs-hero">
          <div className="gs-eyebrow">Get started</div>
          <h1 className="gs-title">
            Welcome to scoutm8{savedName ? (
              <>
                , <em>{savedName}</em>
              </>
            ) : null}
            .
          </h1>
          <p className="gs-sub">
            Short walkthroughs of the main things you can do. Each one opens the real page and shows you
            where to click. You can come back here any time from the sidebar.
          </p>
          <div className="gs-progress" aria-label={`${count} of ${total} done`}>
            {loading ? (
              <Skeleton width={180} height={12} />
            ) : (
              <>
                <div className="gs-bar">
                  <span style={{ width: `${(count / total) * 100}%` }} />
                </div>
                <span className="gs-count">
                  <b>{count}</b> of {total} done
                </span>
              </>
            )}
          </div>
        </header>

        <section className="gs-section">
          <h2 className="gs-h">Start here</h2>
          <div className="gs-main">
            {main.map((s, i) => {
              const isDone = Boolean(done[s.key]);
              const locked = trialLocked(s.key);
              return (
                <div
                  key={s.key}
                  className={`feat ${isDone ? "is-done" : ""} ${s.key === nextKey ? "is-next" : ""} ${locked ? "is-locked" : ""}`}
                >
                  <div className="feat-top">
                    <div className="feat-ic">
                      <s.icon size={22} />
                    </div>
                    {locked ? (
                      <span className="lock-chip">
                        <FiLock size={11} /> Paid plans
                      </span>
                    ) : (
                    <span className={`feat-num ${isDone ? "on" : ""}`}>
                      {isDone ? (
                        <>
                          <FiCheck size={12} /> Done
                        </>
                      ) : (
                        `Step ${i + 1}`
                      )}
                    </span>
                    )}
                  </div>
                  <h3 className="feat-title">{s.title}</h3>
                  <p className="feat-text">{s.text}</p>
                  <ul className="feat-learn">
                    {s.learn.map((l) => (
                      <li key={l}>
                        <FiCheck size={13} /> {l}
                      </li>
                    ))}
                  </ul>
                  <div className="feat-foot">
                    {locked ? (
                      <button type="button" className="btn btn-lg" onClick={() => navigate("/plans")}>
                        See plans
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`btn btn-lg ${!isDone ? "btn-primary" : ""}`}
                        onClick={() => startTour(tourFor(s.tour))}
                      >
                        {isDone ? "Show again" : "Show me how"} <FiArrowRight size={14} />
                      </button>
                    )}
                    <span className="step-where">{s.where}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="gs-section">
          <h2 className="gs-h">Set up your account</h2>
          <ol className="gs-steps">
            {SETUP.map((s, i) => {
              const isDone = Boolean(done[s.key]);
              const locked = trialLocked(s.key);
              const detail = isDone ? s.detail({ settings, savedName, mail }) : null;
              return (
                <li
                  key={s.key}
                  className={`step ${isDone ? "is-done" : ""} ${s.key === nextKey ? "is-next" : ""} ${locked ? "is-locked" : ""}`}
                >
                  <div className="step-num">{locked ? <FiLock size={13} /> : isDone ? <FiCheck size={15} /> : i + main.length + 1}</div>
                  <div className="step-ic">
                    <s.icon size={18} />
                  </div>
                  <div className="step-main">
                    <div className="step-title">{s.title}</div>
                    <div className="step-text">{detail || s.text}</div>
                  </div>
                  <div className="step-where">Settings</div>
                  <div className="step-cta">
                    {locked ? (
                      <>
                        <span className="lock-chip">Paid plans</span>
                        <button type="button" className="btn" onClick={() => navigate("/plans")}>
                          See plans
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className={`btn ${!isDone && s.key === nextKey ? "btn-primary" : ""}`}
                        onClick={() => navigate(`/settings?tab=${s.tab}`)}
                      >
                        {isDone ? "Change" : s.cta} <FiArrowRight size={13} />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        <section className="gs-section">
          <h2 className="gs-h">More you can do</h2>
          <ol className="gs-steps">
            {steps.map((s, i) => {
              const isDone = Boolean(done[s.key]);
              const onlyTrial = trialLocked(s.key);
              const locked = planLocked(s);
              return (
                <li
                  key={s.key}
                  className={`step ${isDone ? "is-done" : ""} ${s.key === nextKey ? "is-next" : ""} ${locked ? "is-locked" : ""}`}
                >
                  <div className="step-num">
                    {locked ? <FiLock size={13} /> : isDone ? <FiCheck size={15} /> : i + main.length + SETUP.length + 1}
                  </div>
                  <div className="step-ic">
                    <s.icon size={18} />
                  </div>
                  <div className="step-main">
                    <div className="step-title">{s.title}</div>
                    <div className="step-text">{s.text}</div>
                  </div>
                  <div className="step-where">{s.where}</div>
                  <div className="step-cta">
                    {locked ? (
                      <>
                        <span className="lock-chip">{onlyTrial ? "Paid plans" : "Growth plan"}</span>
                        <button type="button" className="btn" onClick={() => navigate("/plans")}>
                          See plans
                        </button>
                      </>
                    ) : waiting(s) ? (
                      <button type="button" className="btn" disabled title="Run a search first, then come back here">
                        After your first search
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`btn ${!isDone && s.key === nextKey ? "btn-primary" : ""}`}
                        onClick={() => startTour(tourFor(s.tour))}
                      >
                        {isDone ? "Show again" : "Show me"} <FiArrowRight size={13} />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      </div>
    </Root>
  );
}

const Root = styled.div`
  --font-display: "Instrument Serif", "New York", Georgia, serif;
  --font-body: "Geist", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif;

  margin: -2.5rem;
  min-height: 100dvh;
  background: var(--bg);
  color: var(--ink);
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
  @media (max-width: 1024px) { margin: 0 -1.5rem -1.5rem -1.5rem; }
  @media (max-width: 768px) { margin: 0 -1rem -1rem -1rem; }
  @media (max-width: 450px) { margin: 0 -0.75rem -0.75rem -0.75rem; }

  * { box-sizing: border-box; }

  .gs-wrap { max-width: 980px; margin: 0 auto; padding: 44px 40px 64px; }
  @media (max-width: 768px) { .gs-wrap { padding: 28px 16px 48px; } }

  .gs-eyebrow { font-size: 12px; color: var(--accent); text-transform: uppercase; letter-spacing: .06em; font-weight: 600; margin-bottom: 14px; }
  .gs-title { font-family: var(--font-display); font-weight: 400; font-size: clamp(34px, 5vw, 50px); line-height: 1.05; letter-spacing: -.015em; margin: 0; }
  .gs-title em { font-style: italic; color: var(--accent); }
  .gs-sub { max-width: 600px; margin: 14px 0 0; font-size: 16px; color: var(--ink-soft, var(--ink-muted)); line-height: 1.55; }
  .gs-progress { display: flex; align-items: center; gap: 12px; margin-top: 22px; min-height: 16px; }
  .gs-bar { width: 220px; height: 6px; border-radius: 99px; background: var(--bg-2, var(--line)); overflow: hidden; }
  .gs-bar span { display: block; height: 100%; background: var(--accent); border-radius: inherit; transition: width .4s ease; }
  .gs-count { font-size: 13px; color: var(--ink-muted); }
  .gs-count b { color: var(--ink); font-weight: 600; }

  .gs-section { margin-top: 40px; }
  .gs-h { font-family: var(--font-display); font-weight: 400; font-size: 24px; line-height: 1.1; margin: 0 0 14px; letter-spacing: -.005em; }

  .btn {
    display: inline-flex; align-items: center; gap: 6px; height: 34px; padding: 0 13px;
    border-radius: 9px; border: 1px solid var(--line); background: var(--surface); color: var(--ink);
    font: inherit; font-weight: 500; font-size: 13px; cursor: pointer; white-space: nowrap;
    transition: background .12s, border-color .12s, transform .06s;
  }
  .btn:hover:not(:disabled) { background: var(--surface-tint, var(--bg-2)); border-color: var(--line-strong, var(--line)); }
  .btn:active:not(:disabled) { transform: translateY(.5px); }
  .btn:disabled { opacity: .55; cursor: default; }
  .btn-primary { background: var(--accent); color: var(--on-fill); border-color: var(--accent); }
  .btn-primary:hover:not(:disabled) { background: var(--accent-hover); border-color: var(--accent-hover); }

  .gs-main { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  @media (max-width: 820px) { .gs-main { grid-template-columns: 1fr; } }
  .feat {
    background: var(--surface); border: 1px solid var(--line); border-radius: 16px; padding: 24px 24px 20px;
    display: flex; flex-direction: column; gap: 10px; transition: border-color .15s, box-shadow .15s;
  }
  .feat.is-next { border-color: color-mix(in srgb, var(--accent) 45%, var(--line)); box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 10%, transparent); }
  .feat-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
  .feat-ic { width: 46px; height: 46px; border-radius: 12px; display: grid; place-items: center; color: var(--accent); background: var(--accent-soft, var(--bg-2)); }
  .feat-num { display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: var(--ink-muted); }
  .feat-num.on { color: var(--accent); }
  .feat-title { font-family: var(--font-display); font-weight: 400; font-size: 26px; line-height: 1.1; letter-spacing: -.005em; margin: 0; }
  .feat-text { margin: 0; font-size: 14px; color: var(--ink-soft, var(--ink-muted)); line-height: 1.5; }
  .feat-learn { list-style: none; margin: 4px 0 0; padding: 0; display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: var(--ink); }
  .feat-learn li { display: flex; align-items: center; gap: 8px; }
  .feat-learn svg { color: var(--accent); flex-shrink: 0; }
  .feat-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: auto; padding-top: 12px; }
  .btn-lg { height: 40px; padding: 0 16px; font-size: 14px; border-radius: 10px; }

  .gs-steps { list-style: none; margin: 0; padding: 0; background: var(--surface); border: 1px solid var(--line); border-radius: 14px; overflow: hidden; }
  .step { display: grid; grid-template-columns: 28px 36px 1fr auto auto; align-items: center; gap: 14px; padding: 16px 20px; }
  .step + .step { border-top: 1px solid var(--line); }
  .step.is-next { background: color-mix(in srgb, var(--accent) 5%, transparent); }
  .step-num {
    width: 26px; height: 26px; border-radius: 99px; display: grid; place-items: center;
    font-size: 12px; font-weight: 600; color: var(--ink-muted); border: 1px solid var(--line);
    font-variant-numeric: tabular-nums;
  }
  .step.is-done .step-num { background: var(--accent); border-color: var(--accent); color: var(--on-accent, #fff); }
  .step-ic { width: 36px; height: 36px; border-radius: 10px; display: grid; place-items: center; color: var(--accent); background: var(--accent-soft, var(--bg-2)); }
  .step-main { min-width: 0; }
  .step-title { font-weight: 600; font-size: 14.5px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .step.is-done .step-title { color: var(--ink-muted); }
  .step-text { font-size: 12.5px; color: var(--ink-muted); margin-top: 2px; }
  .step-where { font-size: 11.5px; color: var(--ink-faint, var(--ink-muted)); text-transform: uppercase; letter-spacing: .05em; font-weight: 600; }
  /* Locked: hatched and dashed, content faded to grey; only the lock and See plans stay at full strength. */
  .feat.is-locked, .step.is-locked {
    background:
      repeating-linear-gradient(135deg, transparent 0 9px, color-mix(in srgb, var(--ink) 5%, transparent) 9px 10px),
      color-mix(in srgb, var(--surface) 50%, var(--bg));
  }
  .feat.is-locked { border-style: dashed; border-color: var(--line-strong, var(--line)); }
  .is-locked .feat-ic, .is-locked .feat-title, .is-locked .feat-text, .is-locked .feat-learn, .is-locked .feat-foot .step-where,
  .is-locked .step-ic, .is-locked .step-main, .step.is-locked > .step-where {
    opacity: .38; filter: grayscale(1); cursor: not-allowed; user-select: none;
  }
  .step.is-locked .step-num { background: var(--secondary-soft); border-color: transparent; color: var(--secondary); }
  .lock-chip {
    display: inline-flex; align-items: center; gap: 5px; padding: 4px 9px; border-radius: 99px;
    font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase;
    color: var(--secondary); background: var(--secondary-soft); white-space: nowrap;
  }
  .step-cta { display: flex; align-items: center; gap: 10px; }
  @media (max-width: 680px) {
    .step { grid-template-columns: 28px 1fr; row-gap: 10px; padding: 14px 16px; }
    .step-ic, .step-where { display: none; }
    .step-cta { grid-column: 2; }
  }
`;
